import { Divider, Grid, makeStyles, Typography } from '@material-ui/core';
import { PropTypes } from 'prop-types';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { themeWithoutDir as theme } from '~/modules/App/withRootTheme';
import { NoValue, User } from '~/modules/common/components';
import Date from '~/modules/common/components/Date';
import Decimal from '~/modules/common/components/Decimal';
import { RoleName } from '~/modules/common/components/entities/role';
import ReadOnlyContainer from '~/modules/common/components/ReadOnlyContainer';
import ExtensionFieldValueText from '~/modules/extensionFields/components/ExtensionFieldValueText';
import { useMeContext } from '~/modules/me';
import { ProjectRoleReferenceInfoOnProjectRateTable } from '../common';
import { ReadOnlyCustomFieldValue } from './components';
import TaskDrawerGroupsReadOnly from './TaskDrawerGroupsReadOnly';

const useStyles = makeStyles({
  label: {
    whiteSpace: 'nowrap',
    fontSize: theme.typography.body2.fontSize
  },
  expanded: {
    backgroundColor: theme.palette.background.default
  },
  details: {
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(4),
    paddingBottom: theme.spacing(2),
    marginLeft: theme.spacing(-2),
    marginBottom: 'auto'
  },
  chip: {
    backgroundColor: theme.palette.text.main,
    color: theme.palette.common.white,
    padding: theme.spacing(0.5)
  },
  select: {
    paddingLeft: '5px',
    paddingTop: theme.spacing(1)
  },
  noValue: { fontSize: theme.typography.body2.fontSize },
  roleName: {
    fontSize: theme.typography.body2.fontSize
  },
  description: {
    whiteSpace: 'pre-wrap',
    hyphens: 'auto',
    wordBreak: 'break-word',
    overflowWrap: 'break-word'
  },
  date: {
    lineHeight: 'inherit',
    color: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    display: 'inline'
  },
  container: {
    overflow: 'hidden',
    marginTop: theme.spacing(1.5)
  },
  name: {
    fontSize: theme.spacing(1.5),
    fontWeight: 500,
    paddingLeft: theme.spacing(1) - 1
  },
  roleNameGrid: {
    display: 'flex',
    flexWrap: 'wrap'
  }
});

const taskHeaderDetailsLabels = ({
  isExpenseProductEnabled,
  timeAndExpenseEntryTypeUri,
  costTypeUri
}) => ({
  startDate: <FormattedMessage id="taskDrawerEdit.startDate" />,
  endDate: <FormattedMessage id="taskDrawerEdit.endDate" />,
  role: <FormattedMessage id="taskDrawerEdit.taskRole" />,
  taskOwner: <FormattedMessage id="taskDrawerEdit.taskOwner" />,
  initialEstimatedHours: (
    <FormattedMessage id="taskDrawerEdit.initialEstimatedHours" />
  ),
  timeAndExpenseEntryOptionUri: (
    <FormattedMessage
      id={`timeAndExpenseEntryOptions.${timeAndExpenseEntryTypeUri}`}
    />
  ),
  costTypeUri: <FormattedMessage id={`projectFinancialsCard.${costTypeUri}`} />,
  costType: <FormattedMessage id="taskDrawer.costType" />,
  description: <FormattedMessage id="taskDrawerEdit.description" />,
  timeAndExpenseEntryOptionYes: (
    <FormattedMessage id="timeAndExpenseEntryOptions.yes" />
  ),
  timeEntry: (
    <FormattedMessage
      id={
        isExpenseProductEnabled
          ? 'taskDrawerEdit.timeAndExpenseEntryAllowed'
          : 'taskDrawerEdit.timeEntryAllowed'
      }
    />
  ),
  groupTimesheetAccess: (
    <FormattedMessage id="taskDrawerEdit.groupTimesheetAccess" />
  ),
  timeAndExpenseEntry: (
    <FormattedMessage id="taskDrawerEdit.timeAndExpenseEntry" />
  ),
  allowTimeEntry: {
    label: <FormattedMessage id="taskDrawerEdit.allowTimeEntry.label" />,
    options: {
      yes: <FormattedMessage id="taskDrawerEdit.allowTimeEntry.options.yes" />,
      no: <FormattedMessage id="taskDrawerEdit.allowTimeEntry.options.no" />
    }
  }
});

export const TaskDrawerHeaderInfoBarExpanded = ({
  task,
  visibleCustomFieldDefinitions,
  customFields
}) => {
  const {
    id,
    uri,
    displayText,
    isExpenseProductEnabled,
    hasViewProjectBillingOptions,
    isPortfolioProductEnabled,
    featureFlags: { isPsaPrpGen3PolarisTenantPhase1Enabled } = {},
    tenantPermissions: { isEditTimesheetEnabled }
  } = useMeContext();

  const {
    startDate,
    endDate,
    assignedUser,
    assignedRole,
    extensionFieldValues,
    initialEstimatedHours,
    timeAndExpenseEntryType,
    isTimeEntryAllowed,
    costTypeUri,
    description,
    project
  } = task;

  const classes = useStyles();

  const canViewProject = Boolean(
    project?.permittedActionUris?.includes(
      'urn:replicon:project-action:view-project'
    )
  );

  const canViewCostType = Boolean(
    project?.permittedActionUris?.includes(
      'urn:replicon:project-action:view-cost-type'
    ) &&
      (isPortfolioProductEnabled || isPsaPrpGen3PolarisTenantPhase1Enabled)
  );

  const showTimeAndExpenseEntryTypeField =
    hasViewProjectBillingOptions &&
    project?.billingType?.id !== 'urn:replicon:billing-type:non-billable';

  const noValueClasses = useMemo(
    () => ({
      root: classes.noValue
    }),
    [classes.noValue]
  );

  const taskOwner = useMemo(
    () => (canViewProject ? assignedUser : { id, uri, displayText }),
    [canViewProject, assignedUser, id, uri, displayText]
  );

  const canViewEstimate = Boolean(
    project?.permittedActionUris?.includes(
      'urn:replicon:project-action:view-project-estimates'
    )
  );

  const taskDetailsLabels = taskHeaderDetailsLabels({
    isExpenseProductEnabled,
    timeAndExpenseEntryTypeUri: timeAndExpenseEntryType?.uri,
    costTypeUri
  });

  return (
    <div className={classes.expanded}>
      <Divider />
      <Grid container spacing={1} className={classes.details}>
        <Grid item xs={6}>
          <ReadOnlyContainer label={taskDetailsLabels.startDate}>
            <Date value={startDate} />
          </ReadOnlyContainer>
        </Grid>
        <Grid item xs={6}>
          <ReadOnlyContainer label={taskDetailsLabels.endDate}>
            <Date value={endDate} />
          </ReadOnlyContainer>
        </Grid>
        {showTimeAndExpenseEntryTypeField && (
          <Grid item xs={6}>
            <ReadOnlyContainer label={taskDetailsLabels.timeAndExpenseEntry}>
              <Typography variant="body2">
                {taskDetailsLabels.timeAndExpenseEntryOptionUri}
              </Typography>
            </ReadOnlyContainer>
          </Grid>
        )}
        {isEditTimesheetEnabled && (
          <Grid item xs={6}>
            <ReadOnlyContainer label={taskDetailsLabels.allowTimeEntry.label}>
              <Typography variant="body2">
                {isTimeEntryAllowed
                  ? taskDetailsLabels.allowTimeEntry.options.yes
                  : taskDetailsLabels.allowTimeEntry.options.no}
              </Typography>
            </ReadOnlyContainer>
          </Grid>
        )}
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={6} className={classes.roleNameGrid}>
          <ReadOnlyContainer label={taskDetailsLabels.role}>
            {assignedRole ? (
              <RoleName
                role={assignedRole}
                showTooltip={false}
                showRate={false}
                className={classes.roleName}
              />
            ) : (
              <NoValue classes={noValueClasses} />
            )}
          </ReadOnlyContainer>
          {Boolean(project?.id && task?.id) && (
            <ProjectRoleReferenceInfoOnProjectRateTable
              projectId={project?.id}
              taskUri={task?.id}
              projectRoleId={assignedRole?.id}
            />
          )}
        </Grid>
        <Grid item xs={6}>
          <ReadOnlyContainer label={taskDetailsLabels.taskOwner}>
            {taskOwner ? (
              <User user={taskOwner} />
            ) : (
              <NoValue classes={noValueClasses} />
            )}
          </ReadOnlyContainer>
        </Grid>
        {canViewEstimate && (
          <>
            <Grid item xs={12}>
              <ReadOnlyContainer
                label={taskDetailsLabels.initialEstimatedHours}
              >
                <Typography variant="body2">
                  <Decimal value={initialEstimatedHours} />
                </Typography>
              </ReadOnlyContainer>
            </Grid>
          </>
        )}
        {canViewCostType && (
          <Grid item xs={6}>
            <ReadOnlyContainer label={taskDetailsLabels.costType}>
              <Typography variant="body2" data-qe-id="costType">
                {costTypeUri ? taskDetailsLabels.costTypeUri : <NoValue />}
              </Typography>
            </ReadOnlyContainer>
          </Grid>
        )}
        {isEditTimesheetEnabled && task.timesheetAccessAssignments.length > 0 && (
          <Grid item xs={12}>
            <ReadOnlyContainer label={taskDetailsLabels.groupTimesheetAccess}>
              <TaskDrawerGroupsReadOnly
                assignments={task.timesheetAccessAssignments}
                key="group-timesheet-access-read-only"
              />
            </ReadOnlyContainer>
          </Grid>
        )}
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <ReadOnlyContainer label={taskDetailsLabels.description}>
            <Typography variant="body2" className={classes.description}>
              {description || <NoValue />}
            </Typography>
          </ReadOnlyContainer>
        </Grid>
        {(visibleCustomFieldDefinitions || []).map(definition => (
          <Grid key={definition.uri} item xs={12} sm={6}>
            <ReadOnlyContainer label={definition.displayText}>
              <ReadOnlyCustomFieldValue
                definition={definition}
                value={customFields[definition.uri]}
              />
            </ReadOnlyContainer>
          </Grid>
        ))}
        {(extensionFieldValues || []).map(e => (
          <Grid item xs={12} sm={6} key={e.definition.id}>
            <ReadOnlyContainer label={e.definition.displayText}>
              <Typography variant="body2" className={classes.description}>
                <ExtensionFieldValueText extensionFieldValue={e} />
              </Typography>
            </ReadOnlyContainer>
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

TaskDrawerHeaderInfoBarExpanded.propTypes = {
  task: PropTypes.object.isRequired,
  visibleCustomFieldDefinitions: PropTypes.array,
  customFields: PropTypes.object
};

export default TaskDrawerHeaderInfoBarExpanded;
